import { DEFAULTS } from 'components/Dashboard/constants';
import Styled from 'components/Dashboard/styles';
import useToast from 'hooks/useToast';
import Loading from 'layouts/Loading/loading';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocationAPI from 'services/apis/LocationsAPI';
import UserAPI from 'services/apis/UserAPI';
import Format from 'utils/Formatter';

const PersonalModule = ({ userData, provinces, onUpdate }) => {
  const { t } = useTranslation('dashboard');
  const [person, setPerson] = useState(DEFAULTS.person);
  const [personSelect, setPersonSelect] = useState(DEFAULTS.personSelect);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddress, setIsAddress] = useState(false);
  const [shahrestans, setSharestans] = useState([]);
  const localLocationAPI = new LocationAPI();
  const localUserAPI = new UserAPI();
  const toast = useToast();

  useEffect(() => {
    setPerson({
      birthDate: userData.birthdate || '',
      gender: userData.gender,
      ostan: userData.address?.ostan?.id || 0,
      shahrestan: userData.address?.shahrestan?.id || 0,
      postalCode: userData.address?.postalCode,
      detail: userData.address?.detail
    });
    setPersonSelect({
      ostan: userData.address?.state?.name || '',
      shahrestan: userData.address?.shahrestan?.name || ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (person.state !== 0) {
      if (shahrestans.length > 0) {
        setPersonSelect({ ...personSelect, shahrestan: '' });
        setPerson({ ...person, shahrestan: 0 });
      }
      onGetCities(person.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.state]);

  const onHandleChange = event => {
    if (!event.target.disabled) {
      const _mode = event.target.inputMode;
      const _name = event.target.name;
      const _value =
        _mode === 'numeric'
          ? Format.changeNumberToEn(event.target.value).replace(/\D/, '')
          : event.target.value;

      setPerson({ ...person, [_name]: _value });

      if (['ostan', 'shahrestan'].includes(_name)) {
        const _option =
          event.target.options[event.target.options.selectedIndex].text;
        setPersonSelect({ ...personSelect, [_name]: _option });
      }
    }
  };

  const onGetCities = _province => {
    localLocationAPI.fetchShahrestans(_province).then(response => {
      if (response && Array.isArray(response)) setSharestans(response);
      else toast(t('notification.shahrestan.error'), 'error');
    });
  };

  const onSavePersonal = () => {
    if (isAddress) {
      setIsLoading(true);
      const _body = {
        ...person,
        state: parseInt(person.state),
        shahrestan: parseInt(person.shahrestan)
      };

      localUserAPI.setPersonalInfo(_body).then(response => {
        if (response && response.id) {
          if (onUpdate) onUpdate();
          setTimeout(() => {
            toast(t('notification.personal.success'), 'success');
            setIsLoading(false);
          }, 1000);
        } else {
          setTimeout(() => {
            toast(response.msg || t('notification.personal.error'), 'error');
            setIsLoading(false);
          }, 1000);
        }
      });
    }
  };

  return (
    <Styled.Content>
      <Styled.Title>{t('form.title.personal')}</Styled.Title>
      <Styled.Spacer size={15} />
      <Styled.Fields>
        <Styled.Field>
          <label htmlFor={'firstName'} className={'label'}>
            {t('form.label.firstName')}
          </label>
          <input
            id={'firstName'}
            name={'firstName'}
            className={'input'}
            value={userData?.firstName}
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field>
          <label htmlFor={'lastName'} className={'label'}>
            {t('form.label.lastName')}
          </label>
          <input
            id={'lastName'}
            name={'lastName'}
            className={'input'}
            value={userData?.lastName}
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field>
          <label htmlFor={'mobile'} className={'label'}>
            {t('form.label.mobile')}
          </label>
          <input
            id={'mobile'}
            name={'mobile'}
            className={'input wide ltr'}
            value={Format.formatPhoneNumber(`0${userData?.mobile}`)}
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field>
          <label htmlFor={'nationalCode'} className={'label'}>
            {t('form.label.nationalCode')}
          </label>
          <input
            id={'nationalCode'}
            name={'nationalCode'}
            className={'input wide'}
            value={userData?.nationalCode}
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field>
          <label htmlFor={'idCardNumber'} className={'label'}>
            {t('form.label.idCardNumber')}
          </label>
          <input
            id={'idCardNumber'}
            name={'idCardNumber'}
            className={'input wide'}
            value={userData?.idCardNumber}
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field>
          <label htmlFor={'birthDate'} className={'label'}>
            {t('form.label.birthDate')}
          </label>
          <input
            id={'birthDate'}
            name={'birthDate'}
            className={'input'}
            value={person.birthDate}
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field>
          <label htmlFor={'gender'} className={'label'}>
            {t('form.label.gender')}
          </label>
          <input
            id={'gender'}
            name={'gender'}
            className={'input'}
            value={
              person.gender === 'male'
                ? t('form.gender.male')
                : person.gender === 'female'
                ? t('form.gender.female')
                : ''
            }
            disabled={true}
          />
        </Styled.Field>

        <Styled.Field size={'full'}>
          <label htmlFor={'address'} className={'label'}>
            {t('form.label.address')}
          </label>
          <input
            id={'address'}
            name={'address'}
            className={`input ${isAddress ? 'active' : ''}`}
            value={
              !person.detail
                ? ''
                : t('form.full_address', {
                    state: personSelect.state,
                    shahrestan: personSelect.shahrestan,
                    detail: person.detail,
                    postalCode: person.postalCode
                  })
            }
            disabled={true}
          />
          <span
            className={`action ${isAddress ? 'active' : ''}`}
            onClick={() => setIsAddress(!isAddress)}
          >
            <svg viewBox="0 0 26 26">
              <g clipPath="url(#clip0)">
                <path d="M23.997 16.333C23.6393 16.333 23.3494 16.6229 23.3494 16.9805V22.7299C23.3482 23.8024 22.4793 24.6715 21.4068 24.6725H3.23767C2.16519 24.6715 1.29633 23.8024 1.29507 22.7299V5.85584C1.29633 4.78361 2.16519 3.9145 3.23767 3.91323H8.98707C9.34474 3.91323 9.63461 3.62336 9.63461 3.2657C9.63461 2.90829 9.34474 2.61816 8.98707 2.61816H3.23767C1.45038 2.62019 0.00202355 4.06854 0 5.85584V22.7302C0.00202355 24.5175 1.45038 25.9658 3.23767 25.9679H21.4068C23.1941 25.9658 24.6425 24.5175 24.6445 22.7302V16.9805C24.6445 16.6229 24.3546 16.333 23.997 16.333Z" />
                <path d="M24.3824 0.853494C23.2444 -0.284498 21.3994 -0.284498 20.2614 0.853494L8.70899 12.4059C8.62982 12.4851 8.57265 12.5832 8.54281 12.691L7.02363 18.1756C6.96115 18.4004 7.02464 18.6412 7.18956 18.8064C7.35473 18.9713 7.59553 19.0348 7.8204 18.9726L13.305 17.4531C13.4127 17.4233 13.5109 17.3661 13.59 17.287L25.1422 5.73429C26.2784 4.59554 26.2784 2.75209 25.1422 1.61334L24.3824 0.853494ZM10.1199 12.8268L19.5747 3.3718L22.6239 6.42103L13.1689 15.876L10.1199 12.8268ZM9.51082 14.049L11.9469 16.4854L8.57721 17.419L9.51082 14.049ZM24.2266 4.81863L23.5398 5.50537L20.4903 2.45589L21.1773 1.76915C21.8094 1.13704 22.8344 1.13704 23.4665 1.76915L24.2266 2.52899C24.8576 3.16185 24.8576 4.18602 24.2266 4.81863Z" />
                {isAddress && (
                  <path d="M10.1258 12.9255L19.5805 3.47043L22.6298 6.51966L13.1747 15.9747L10.1258 12.9255Z" />
                )}
              </g>
              <defs>
                <clipPath id="clip0">
                  <rect width="26" height="26" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
        </Styled.Field>

        {isAddress && (
          <>
            <Styled.Field field={'select'} isActive={true}>
              <label htmlFor={'state'} className={'label'}>
                {t('form.label.ostan')}
              </label>
              <select
                id={'state'}
                name={'state'}
                className={'input center'}
                value={person.state}
                disabled={!isAddress}
                onChange={onHandleChange}
              >
                <option value={0} disabled={true} hidden={true} />
                {provinces.map((_province, _index) => (
                  <option key={`state_item_${_index}`} value={_province.id}>
                    {_province.name}
                  </option>
                ))}
              </select>
            </Styled.Field>

            <Styled.Field field={'select'} isActive={true}>
              <label htmlFor={'shahrestan'} className={'label'}>
                {t('form.label.shahrestan')}
              </label>
              <select
                id={'cishahrestanty'}
                name={'shahrestan'}
                className={'input center'}
                value={person.shahrestan}
                disabled={!isAddress || person.state === 0}
                onChange={onHandleChange}
              >
                <option value={0} disabled={true} hidden={true} />
                {shahrestans.map((_city, _index) => (
                  <option key={`city_item_${_index}`} value={_city.id}>
                    {_city.name}
                  </option>
                ))}
              </select>
            </Styled.Field>

            <Styled.Field isActive={true}>
              <label htmlFor={'postalCode'} className={'label'}>
                {t('form.label.postalCode')}
              </label>
              <input
                id={'postalCode'}
                name={'postalCode'}
                className={'input wide center'}
                value={person.postalCode}
                disabled={!isAddress}
                maxLength={10}
                inputMode={'numeric'}
                onChange={onHandleChange}
              />
            </Styled.Field>

            <Styled.Field size={'full'} isActive={true}>
              <label htmlFor={'detail'} className={'label'}>
                {t('form.label.detail')}
              </label>
              <input
                id={'detail'}
                name={'detail'}
                className={'input center'}
                value={person.detail}
                disabled={!isAddress}
                onChange={onHandleChange}
              />
            </Styled.Field>
          </>
        )}

        <Styled.Action size={'full'}>
          <Styled.SaveButton isDisabled={!isAddress} onClick={onSavePersonal}>
            {isLoading ? <Loading color={'#fff'} /> : t('action.save_changes')}
          </Styled.SaveButton>
        </Styled.Action>
      </Styled.Fields>
    </Styled.Content>
  );
};

export default PersonalModule;
